// src/packages/chubb/pa/chubb_pa_series_e_2025/ChubbPASeriesEDetails.jsx
import React from "react";
import "./chubb_pa_series_e.css";

/**
 * Chubb — PA Series E (Plan 1–8 + Kid)
 * Zero-index safe: ไม่มี {index} ใน JSX และทุก .map ใช้คีย์จากข้อมูลจริง
 * เติมตัวเลขจากใบปลิว Series E ลงในอาร์เรย์ด้านล่างได้ทันที
 */

const fmt = (x) =>
  x === null || x === undefined ? "—" : typeof x === "number" ? x.toLocaleString() : x;

const PLANS = ["แผน 1","แผน 2","แผน 3","แผน 4","แผน 5","แผน 6","แผน 7","แผน 8","บุตร Kid"];

// ===== ผลประโยชน์ต่อแผน (ตัวเลขตัวอย่างตามลำดับก้าวหน้า — โปรดแก้ให้ตรงใบปลิว) =====
const benefits = [
  { key: "life",    name: "1. อุบัติเหตุทั่วไป (ชีวิต/สูญเสียอวัยวะ/ทุพพลภาพถาวร)", vals: [100000,200000,300000,500000,700000,1000000,2000000,3000000,50000] },
  { key: "murder",  name: "1.2 ฆาตกรรม/ทำร้ายร่างกาย",                        vals: [100000,200000,300000,500000,700000,1000000,2000000,3000000,50000] },
  { key: "moto",    name: "1.3 อุบัติเหตุรถจักรยานยนต์",                       vals: [50000,100000,150000,250000,350000,500000,1000000,1500000,25000] },
  { key: "terror",  name: "1.4 การก่อการร้าย",                                  vals: [100000,200000,300000,500000,700000,1000000,2000000,3000000,50000] },
  { key: "holiday", name: "1.5/1.6 วันหยุด/พาหนะสาธารณะ (เพิ่มทุน)",           vals: [100000,200000,300000,500000,700000,1000000,2000000,3000000,50000] },
  { key: "funeral", name: "2. ค่าปลงศพ (เหตุอุบัติเหตุ)",                      vals: [/* 20,000 ทุกแผนยกเว้น Kid ~ 5,000 */ 20000,20000,20000,20000,20000,20000,20000,20000,5000] },
  { key: "med",     name: "2.2 ค่ารักษาพยาบาลต่อครั้ง (MED)",                   vals: [5000,10000,15000,20000,25000,30000,35000,20000,5000] }, // แก้ให้ตรงใบปลิว
  { key: "hap",     name: "2.3 HAP (รายได้รายเดือน/Smart Money/ICU ถ้ามี)",     vals: [/* ใส่ค่าตามแผนหรือคงเป็น '—' */] },
  { key: "edu",     name: "2.7 กองทุนการศึกษาบุตร (ถ้ามี)",                     vals: [/* แผน Kid/หรือบางแผนเท่านั้น */] },
];

// ===== เบี้ยรายปี (รวมอากร/แสตมป์) — PA1 และ PA2 (ดูสามบรรทัดแถบแดงล่างใบปลิว) =====
const premiums = {
  PA1: [
    { key: "main",   label: "ผู้เอาประกันหลัก (PA1)",                     vals: [3509,3890,4290,4590,5190,6390,7190,9090,null] },
    { key: "spouse", label: "ผู้เอาประกัน + คู่สมรส (PA1)",                 vals: [5890,6890,7390,8290,8890,9120,12290,13690,null] },
    { key: "kids2",  label: "ผู้เอาประกัน + บุตร ≤ 2 คน (PA1)",             vals: [7090,7390,7390,7890,8690,8960,10690,12590,null] },
    { key: "kids3",  label: "ผู้เอาประกัน + บุตร ≤ 3 คน (PA1)",             vals: [10390,10890,11790,12390,13490,15790,17390,21190,null] },
  ],
  PA2: [
    { key: "main",   label: "ผู้เอาประกันหลัก (PA2)",                     vals: [3830,4190,4790,5090,5590,6990,7790,9990,null] },
    { key: "spouse", label: "ผู้เอาประกัน + คู่สมรส (PA2)",                 vals: [6590,7390,8080,8960,9890,11590,12990,15990,null] },
    { key: "kids2",  label: "ผู้เอาประกัน + บุตร ≤ 2 คน (PA2)",             vals: [8190,8960,9690,10390,11390,12990,14590,17990,null] },
    { key: "kids3",  label: "ผู้เอาประกัน + บุตร ≤ 3 คน (PA2)",             vals: [11290,11890,13090,13690,14690,17390,18590,23190,null] },
  ],
};

export default function ChubbPASeriesEDetails({ onQuote }) {
  return (
    <div className="pkg-detail chubb-pa-e">
      <h2>Chubb PA Series E — แผน 1–8 + Kid</h2>
      <p className="pkg-note">
        อายุรับ 15–70 ปี ต่ออายุได้ถึง 75 ปี • บุตร 6 เดือน–23 ปี (ตามเงื่อนไข) •
        เดี่ยว/คู่สมรส/ครอบครัว • เบี้ยรวมอากรแสตมป์
      </p>

      {/* Benefits */}
      <h3 className="pkg-section">ผลประโยชน์ต่อแผน</h3>
      <table className="pkg-table">
        <thead>
          <tr>
            <th className="pkg-left">ความคุ้มครอง</th>
            {PLANS.map((p) => <th key={`plan-${p}`}>{p}</th>)}
          </tr>
        </thead>
        <tbody>
          {benefits.map((b) => (
            <tr key={`bf-${b.key}`}>
              <td className="pkg-left">{b.name}</td>
              {(b.vals || []).map((v, i) => <td key={`bf-${b.key}-${i}`}>{fmt(v)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Premiums: PA1 */}
      <h3 className="pkg-section">เบี้ยรายปี (PA1)</h3>
      <table className="pkg-table">
        <thead>
          <tr>
            <th className="pkg-left">รูปแบบผู้เอาประกัน</th>
            {PLANS.map((p) => <th key={`pa1h-${p}`}>{p}</th>)}
          </tr>
        </thead>
        <tbody>
          {premiums.PA1.map((row) => (
            <tr key={`pa1-${row.key}`}>
              <td className="pkg-left">{row.label}</td>
              {row.vals.map((v, i) => <td key={`pa1-${row.key}-${i}`}>{fmt(v)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>

      {/* Premiums: PA2 */}
      <h3 className="pkg-section">เบี้ยรายปี (PA2)</h3>
      <table className="pkg-table">
        <thead>
          <tr>
            <th className="pkg-left">รูปแบบผู้เอาประกัน</th>
            {PLANS.map((p) => <th key={`pa2h-${p}`}>{p}</th>)}
          </tr>
        </thead>
        <tbody>
          {premiums.PA2.map((row) => (
            <tr key={`pa2-${row.key}`}>
              <td className="pkg-left">{row.label}</td>
              {row.vals.map((v, i) => <td key={`pa2-${row.key}-${i}`}>{fmt(v)}</td>)}
            </tr>
          ))}
        </tbody>
      </table>

      <div className="pkg-foot">
        <p className="pkg-note">* โปรดตรวจสอบข้อยกเว้น/อาชีพไม่รับก่อนเสนอขาย</p>
        <button className="pkg-quote-btn" onClick={onQuote} aria-label="ขอใบเสนอราคา Chubb PA Series E">
          ขอใบเสนอราคา
        </button>
      </div>
    </div>
  );
}
